import type { SeatData } from '@/components/grid';
import { getHighResAvatarUrl } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import {
  V2_CONSTANTS,
  type V2Grid,
  type V2GridAsset,
  type V2GridSeat,
  type V2GridSeatEvent,
} from '@/lib/types/v2';
import type { LiveEvent } from '@/hooks/use-live-events';
import { WETH_ADDRESS, isV22Grid } from '@/lib/constants/addresses';
import { runwaySeconds as calcRunwayFromBalance, taxAdjustedBalance } from '@/lib/tax';
import { safeBigInt } from '@/lib/bigint';
import { getDutchEndsAt } from '@/lib/contracts/dutch';

export { WETH_ADDRESS };

export const BASE_SYMBOLS = new Set(['ETH', 'WETH', 'FLETH', 'USDC', 'USDT']);
export const KNOWN_YIELD_SYMBOLS: Record<string, string> = {
  '0x4200000000000000000000000000000000000006': 'WETH',
  '0x0000000000000000000000000000000000000000': 'ETH',
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': 'USDC',
  '0x4200000000000000000000000000000000000001': 'WETH',
};

export const KNOWN_YIELD_DECIMALS: Record<string, number> = {
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': 6, // USDC
};

/**
 * Envio / SSE timestamps occasionally arrive as unix milliseconds while the
 * rest of the stack assumes seconds (`getDutchPrice`, anti-snipe math).
 * Values above ~year-2288 in *seconds* are impossible on-chain today — treat
 * them as ms and floor-divide.
 */
export function normalizeUnixSeconds(ts: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  if (ts > 1e12) return Math.floor(ts / 1000);
  return Math.floor(ts);
}

export type PriorityHolder = {
  address: string;
  heldSince: number;
  displayName?: string;
  avatarUrl?: string;
  level?: number;
};

/**
 * Parse a v2.2 `Grid.initialPricesPacked` string into a per-seat price
 * array. The indexer serialises the array as a comma-separated decimal
 * string (each entry is a uint128 in token base units). Empty / malformed
 * inputs yield an empty array — callers fall back to "no synthesis".
 *
 * See `takeover-indexer/src/handlers/v2/ugm.ts::GridPricesInitialized`
 * for the producer side.
 */
export function parseInitialPricesPacked(
  packed: string | null | undefined,
): bigint[] {
  if (!packed) return [];
  const parts = packed.split(',');
  const out: bigint[] = [];
  for (const raw of parts) {
    const trimmed = raw.trim();
    if (!trimmed) {
      out.push(0n);
      continue;
    }
    try {
      out.push(BigInt(trimmed));
    } catch {
      out.push(0n);
    }
  }
  return out;
}

/**
 * Overlay creator-held synthetic rows on top of unmaterialised v2.2 seats.
 *
 * Drops in transparently to existing call sites — for v2.1 grids the
 * input is returned unchanged. Synthesises a seat when (and only when)
 * **all** of the following hold for a row at index `i`:
 *
 *   1. The grid is v2.2 (callers gate on `isV22GridVersion(gridVersion)`).
 *   2. The row's `holder` is null/zero AND `deposit === '0'` AND
 *      `selfAssessedPrice === '0'` — i.e. the server-side route returned
 *      an "empty" placeholder because the indexer has no `GridSeat` row
 *      for this index yet.
 *   3. `initialPrices[i]` is defined — meaning we have a creator-set
 *      reserve price to render.
 *
 * This is the most permissive condition that won't clobber a real
 * indexed row. Any seat that has *any* on-chain interaction (price set,
 * deposit added, bought) is materialised and indexed; the indexer fills
 * `selfAssessedPrice` and `deposit` accordingly, so it can never look
 * "empty" by these criteria.
 *
 * Synthesised seats carry the creator as holder, the indexer's stored
 * initial price, a zero deposit, and `isLazy: true`. They are
 * tax-exempt by construction (`isCreatorSeat` short-circuits tax math
 * when `deposit === 0n`), matching the on-chain semantics where
 * unmaterialised V2.2 seats accrue no tax until first interaction.
 *
 * @param rawSeats         Seat rows from `/api/grid/...` (length = totalSeats).
 * @param creator          `Grid.creator` — assigned as the synthetic holder.
 * @param initialPrices    Decoded `Grid.initialPricesPacked`.
 * @param gridCreatedAt    `Grid.createdAt` (unix-seconds) — used for
 *                         `heldSince` / `lastTaxTime` so synth rows look
 *                         consistent with V2.1 eager materialisation.
 */
export function synthesizeUnmaterializedSeats(
  rawSeats: V2GridSeat[],
  args: {
    creator: string;
    initialPrices: bigint[];
    gridCreatedAt: number;
  },
): V2GridSeat[] {
  const { creator, initialPrices, gridCreatedAt } = args;
  if (initialPrices.length === 0) return rawSeats;
  if (rawSeats.length === 0) return rawSeats;

  return rawSeats.map((seat) => {
    const idx = seat.seatIndex;
    if (idx < 0 || idx >= initialPrices.length) return seat;

    // Heuristic for "indexer has no row for this index yet":
    // - holder null/zero AND deposit zero AND price zero.
    // The server route's empty-fallback at /api/grid/[ugm]/[gridId]
    // returns exactly this shape for unindexed indices on V2.2 grids
    // (where the on-chain `getSeat()` also reads zero-default until
    // `_materializeSeat` runs). Any indexed or RPC-recovered row has at
    // least a non-null holder.
    const noHolder = !seat.holder || seat.holder === '0x0000000000000000000000000000000000000000';
    if (!noHolder) return seat;
    if (seat.deposit !== '0' && seat.deposit !== '0x0') return seat;
    if (seat.selfAssessedPrice !== '0' && seat.selfAssessedPrice !== '0x0') return seat;

    const initialPrice = initialPrices[idx];
    return {
      ...seat,
      holder: creator as `0x${string}`,
      selfAssessedPrice: initialPrice.toString(),
      deposit: '0',
      lastTaxTime: seat.lastTaxTime || gridCreatedAt,
      lastPriceChangeTime: seat.lastPriceChangeTime || gridCreatedAt,
      heldSince: seat.heldSince || gridCreatedAt,
      isActive: true,
      createdAt: seat.createdAt || gridCreatedAt,
      isLazy: true,
    };
  });
}

export function mergeRecentGridEvents(
  apiRecentEvents: V2GridSeatEvent[],
  liveEvents: LiveEvent[],
  gridId: string
): V2GridSeatEvent[] {
  if (liveEvents.length === 0) return apiRecentEvents;

  const apiEventIds = new Set(apiRecentEvents.map((e) => e.id));
  const gridIdLower = gridId.toLowerCase();
  // Defensive double-belt over the `useLiveGrid` ref-guard short-circuit:
  // drop any live event whose `poolId` doesn't match the current grid
  // before we re-stamp it with `gridId`. Without this, a stale event
  // that survives the guard (e.g. a future regression that lets the
  // hook return data ahead of `syncedPoolIdRef`) would still pollute
  // `forfeitTimeBySeat` and surface as ghost DrainShield / forfeit
  // visuals on indices the new board doesn't actually have those
  // states on. The SSE handler in `useLiveGrid` already filters by
  // `poolId` on receive (`use-live-grid.ts:307`), so live events that
  // make it into `state.events` always carry their own `poolId` —
  // this is the consumer-side mirror of that filter.
  const converted: V2GridSeatEvent[] = liveEvents
    .filter(
      (e) =>
        !apiEventIds.has(e.id) &&
        e.poolId?.toLowerCase() === gridIdLower,
    )
    .map((e) => ({
      id: e.id,
      seatId: e.seatId,
      gridId,
      eventType: e.eventType as V2GridSeatEvent['eventType'],
      user: e.user as `0x${string}`,
      timestamp: normalizeUnixSeconds(e.timestamp),
      blockNumber: e.blockNumber ?? 0,
      txHash: e.txHash ?? '',
      price: e.price,
      amount: e.amount,
      oldHolder: e.oldHolder as `0x${string}` | undefined,
      newHolder: e.newHolder as `0x${string}` | undefined,
      refund: e.refund,
      newPrice: e.newPrice,
    }));

  if (converted.length === 0) return apiRecentEvents;
  return [...converted, ...apiRecentEvents].sort((a, b) => b.timestamp - a.timestamp);
}

export function buildForfeitTimeBySeat(
  recentEvents: V2GridSeatEvent[],
  rawSeats: V2GridSeat[]
): Map<number, number> {
  const map = new Map<number, number>();

  for (const evt of recentEvents) {
    if (evt.eventType === 'FORFEITED' && evt.seatId !== undefined) {
      const parts = evt.seatId.split('-');
      const seatIdx = parseInt(parts[parts.length - 1], 10);
      const ts = normalizeUnixSeconds(evt.timestamp);
      const existing = map.get(seatIdx) ?? 0;
      if (ts > existing) map.set(seatIdx, ts);
    }
  }

  for (const seat of rawSeats) {
    const ft = map.get(seat.seatIndex);
    const heldSince = seat.heldSince || seat.createdAt || 0;
    if (ft && ft < heldSince) {
      map.delete(seat.seatIndex);
    }
  }

  return map;
}

/**
 * Latest `FORFEITED.oldHolder` per seat (same timestamp tie-break + heldSince
 * invalidation as {@link buildForfeitTimeBySeat}). Used so vacant Dutch /
 * anti-snipe reclaim tiles keep the forfeited user's pedestal material until
 * the window closes.
 */
export function buildForfeitOldHolderBySeat(
  recentEvents: V2GridSeatEvent[],
  rawSeats: V2GridSeat[],
): Map<number, string> {
  const tsMap = new Map<number, number>();
  const holderMap = new Map<number, string>();

  for (const evt of recentEvents) {
    if (evt.eventType !== 'FORFEITED' || evt.seatId === undefined) continue;
    const parts = evt.seatId.split('-');
    const seatIdx = parseInt(parts[parts.length - 1], 10);
    const ts = normalizeUnixSeconds(evt.timestamp);
    const existingTs = tsMap.get(seatIdx) ?? 0;
    if (ts > existingTs) {
      tsMap.set(seatIdx, ts);
      const oh = evt.oldHolder;
      const zero = '0x0000000000000000000000000000000000000000'.toLowerCase();
      if (oh && oh.toLowerCase() !== zero) {
        holderMap.set(seatIdx, oh);
      } else {
        holderMap.delete(seatIdx);
      }
    }
  }

  for (const seat of rawSeats) {
    const ft = tsMap.get(seat.seatIndex);
    const heldSince = seat.heldSince || seat.createdAt || 0;
    if (ft && ft < heldSince) {
      holderMap.delete(seat.seatIndex);
    }
  }

  return holderMap;
}

export function buildSeatData(args: {
  rawSeats: V2GridSeat[];
  address?: string | null;
  /**
   * Every wallet linked to the viewer's Privy session, lowercased.
   *
   * `address` is the wagmi-active wallet — i.e. the one that will sign
   * a transaction right now. For ownership/identity flags driven by
   * "is this seat held by the connected viewer" (`isOwn`,
   * `isOwnReservation`) we want a broader signal: a Privy user with
   * a V1-snapshot EOA AND a Twitter-provisioned embedded Privy wallet
   * has only one of those active in wagmi at a time, but BOTH are
   * "the viewer". Without this, switching to the embedded wallet
   * silently drops the purple-rim affordance on a tile reserved for
   * the EOA — exactly the symptom on grid `0x5089…-17` for
   * `0x0A1461…` (a V1 holder who logs in via Twitter).
   *
   * Optional for back-compat: callers that don't pass it (tests,
   * non-lobby surfaces) fall back to the strict `address`-only match
   * preserved alongside the union below.
   */
  viewerAddresses?: ReadonlySet<string>;
  avatarUrl?: string | null;
  displayName?: string | null;
  userLevel?: number;
  taxRateBps?: number;
  creator?: string;
  graduated?: boolean;
  forfeitTimeBySeat: Map<number, number>;
  /**
   * V21 anti-snipe priority window in seconds (5 min on Base mainnet).
   * Ignored for V22 grids — those source the forfeit window from
   * `forfeitureDuration` (per-grid Dutch decay window) instead and feed
   * `getDutchEndsAt`. Kept as a separate prop so the V21 path is
   * untouched and the V22 path can fall back to
   * `V2_CONSTANTS.V22_DEFAULT_FORFEITURE_DURATION` (600s) when the
   * per-grid override is 0.
   */
  antiSnipeWindow: number;
  /**
   * Manager (UGM) contract address for the grid these seats belong to.
   * Drives V22 vs V21 detection in the seat-data builder so the
   * `antiSnipeEndsAt` field can carry the correct forfeit-window
   * end-time semantics (V21: priority waterfall end. V22: Dutch
   * clearing-price-hits-zero end). Drawers / overlays gate
   * priority-only UI on the negative case (`!isV22Grid(managerAddress)`)
   * — see claim-drawer / bulk-action-drawer / seat-cell.
   */
  managerAddress?: string | null;
  /**
   * V22-only — `Grid.forfeitureDuration` in seconds. Per-grid Dutch
   * decay window. `0` (or undefined) falls back to
   * `V2_CONSTANTS.V22_DEFAULT_FORFEITURE_DURATION` (600s) — matches
   * the contract's `DEFAULT_FORFEITURE_DURATION` fallback in
   * `_dutchPrice`.
   */
  forfeitureDuration?: number;
  reservations?: { seatIndex: number; claimant: string; claimantIdentity?: { displayName?: string; avatarUrl?: string; level?: number } }[];
  claimDeadline?: number;
  /**
   * Per-seat `oldHolder` from the winning FORFEITED event (see
   * {@link buildForfeitOldHolderBySeat}). Vacant tiles during an open reclaim
   * window use this for pedestal material continuity.
   */
  forfeitOldHolderBySeat?: Map<number, string>;
}): SeatData[] {
  const {
    rawSeats,
    address,
    viewerAddresses,
    avatarUrl,
    displayName,
    userLevel,
    taxRateBps,
    creator,
    graduated,
    forfeitTimeBySeat,
    antiSnipeWindow,
    managerAddress,
    forfeitureDuration,
    reservations,
    claimDeadline,
    forfeitOldHolderBySeat = new Map(),
  } = args;

  // V22 vs V21 forfeit-window semantics. Latched once per call rather
  // than per-seat — every seat in `rawSeats` belongs to the same
  // `managerAddress`, and `isV22Grid` is a Set lookup but we'd be
  // hitting it ~100 times per call for nothing.
  const isV22 = isV22Grid(managerAddress);
  const v22Window =
    forfeitureDuration && forfeitureDuration > 0
      ? forfeitureDuration
      : V2_CONSTANTS.V22_DEFAULT_FORFEITURE_DURATION;

  const now = Math.floor(Date.now() / 1000);
  // Fall back to V2's classic 100-seat shape when the caller passed an
  // empty `rawSeats` array. The 100 here mirrors `V2_CONSTANTS.TOTAL_SEATS`
  // (legacy v2.0/v2.1 grids); v2.3 grids set `totalSeats` explicitly so
  // this branch never fires for them. We deliberately don't fan out to
  // `MAX_TOTAL_SEATS` (4096) here — that would over-allocate empty rows
  // for the lobby's loading state.
  if (!rawSeats.length) return Array.from({ length: V2_CONSTANTS.TOTAL_SEATS }, (_, i) => createEmptySeat(i));

  // `?? 500` is not enough: `NaN` is non-nullish, so a corrupted
  // `taxRateBps` from a partial cache merge would propagate and
  // `BigInt(NaN)` throws synchronously inside this per-seat map —
  // the same global-error surface as bare `BigInt` on seats.
  const taxRateBpsBn = safeBigInt(taxRateBps, 500n);
  const creatorLower = creator?.toLowerCase();
  const weekSeconds = BigInt(V2_CONSTANTS.SECONDS_PER_WEEK);

  const reservationMap = new Map<number, { claimant: string; claimantIdentity?: { displayName?: string; avatarUrl?: string; level?: number } }>();
  if (reservations && claimDeadline && claimDeadline > now) {
    for (const r of reservations) {
      reservationMap.set(r.seatIndex, r);
    }
  }

  const ZERO_ADDR = '0x0000000000000000000000000000000000000000'.toLowerCase();

  return rawSeats.map((seat) => {
    // `safeBigInt` defends against malformed seat payloads (null /
    // empty string / `'0.0'` / scientific notation) that have shown
    // up from partial SSE patches and stale Envio rows. A bare
    // `BigInt()` would throw synchronously inside this render-path
    // map and propagate to `(lobby)/error.tsx`, taking the whole
    // lobby down for a single bad seat. See `lib/bigint.ts`.
    const price = safeBigInt(seat.selfAssessedPrice);
    const deposit = safeBigInt(seat.deposit);

    const rpcHolderOverlay = seat.rpcOnChainHolder;
    const resolvedHolder: `0x${string}` | null =
      rpcHolderOverlay !== undefined
        ? rpcHolderOverlay && rpcHolderOverlay.toLowerCase() !== ZERO_ADDR
          ? rpcHolderOverlay
          : null
        : seat.holder && seat.holder.toLowerCase() !== ZERO_ADDR
          ? seat.holder
          : null;

    // Single source of truth for "this seat is held by someone". Every
    // downstream flag (`isForfeited` / `isPendingForfeit` /
    // `isCreatorSeat` / `isOwn`) and every tier consumer (`tileTier`)
    // funnels through this Boolean coercion so a falsy `holder` value
    // (null / undefined / `''`) can never produce an "occupied" flag
    // on a seat the rest of the app sees as vacant. The previous
    // `seat.holder !== null` strict check was asymmetric with
    // `tileTier`'s `!seat.holder` and surfaced as: a raw seat with
    // `holder: ''` (e.g. an indexer/RPC race writing the zero-address
    // sentinel as empty string) would yield `output.holder = null`
    // (via `seat.holder || null` below) AND
    // `output.isPendingForfeit = true`, which the 2D renderer paints
    // as `<ShieldAlert>` + amber dashed border on a tile that 3D
    // (and the rest of the codebase) treats as vacant. See
    // `LobbySeatTile2D` `isVacant && isPendingForfeit` for the visual
    // path that's now structurally impossible.
    const hasHolder = !!resolvedHolder;
    const isCreatorSeat =
      !!creatorLower &&
      resolvedHolder?.toLowerCase() === creatorLower &&
      deposit === 0n &&
      !graduated;

    const lastTax = seat.lastTaxTime || seat.heldSince || seat.createdAt || now;

    // Prefer authoritative RPC currentBalance when the API provides it.
    // CRUCIALLY we also decay it forward by the wall-clock time since
    // the multicall sampled it (`seat.rpcCurrentBalanceAt`). Without
    // that decay the runway display would freeze at the multicall
    // value between API refreshes — combined with a Redis cache hit
    // and React Query's `staleTime`, that frozen window can stretch
    // to ~25s, which is what surfaced as "59 minutes left, no
    // reduction anywhere" on the user's reported seat.
    //
    // When the server-side stamp is missing (legacy cached payloads,
    // tests, the post-tx writes from `updateSeatsInCache` before that
    // file picked up the field) we fall back to "no decay applied" —
    // matches the pre-fix behaviour, so adding the field is purely
    // additive on the rest of the stack.
    const hasRpcBalance = seat.rpcCurrentBalance !== undefined;
    let currentDeposit: bigint;
    let runwaySeconds: number;

    if (hasRpcBalance && !isCreatorSeat) {
      const rpcBal = safeBigInt(seat.rpcCurrentBalance);
      const rpcSampledAt = seat.rpcCurrentBalanceAt ?? now;
      const decayed = rpcBal > 0n
        ? taxAdjustedBalance(rpcBal, price, taxRateBpsBn, rpcSampledAt, now)
        : 0n;
      currentDeposit = decayed;
      runwaySeconds = calcRunwayFromBalance(currentDeposit, price, taxRateBpsBn);
    } else {
      const weeklyTax = isCreatorSeat
        ? 0n
        : (price * taxRateBpsBn) / BigInt(V2_CONSTANTS.TAX_RATE_DENOMINATOR);
      const elapsed = BigInt(Math.max(0, now - lastTax));
      const pendingTax = weeklyTax > 0n ? (weeklyTax * elapsed) / weekSeconds : 0n;
      currentDeposit = deposit > pendingTax ? deposit - pendingTax : 0n;
      runwaySeconds = weeklyTax > 0n ? Number((currentDeposit * weekSeconds) / weeklyTax) : 0;
    }

    const isOwn = !!address && resolvedHolder?.toLowerCase() === address.toLowerCase();

    const indexerForfeitTs = normalizeUnixSeconds(
      forfeitTimeBySeat.get(seat.seatIndex) ?? 0,
    );
    const rpcForfeitTs = normalizeUnixSeconds(seat.rpcForfeitedAt ?? 0);
    const effectiveForfeitTs = Math.max(indexerForfeitTs, rpcForfeitTs);
    const hasForfeitSignal =
      indexerForfeitTs > 0 ||
      rpcForfeitTs > 0;

    const reservation = reservationMap.get(seat.seatIndex);
    // V22 lazy seats: `UnifiedGridManagerV22.seats(gridId, idx)` returns the
    // grid creator as the seat holder when the seat hasn't been materialized
    // yet (`_lazyHolder` fallback in the contract). The API faithfully
    // surfaces that as `seat.holder = creator` via `fetchOnChainSeats`, so
    // every unclaimed reservation slot arrives here with a creator-shaped
    // `resolvedHolder`. Without this branch the original `!resolvedHolder`
    // gate would always fail, suppressing `isReserved` for whitelist grids
    // and painting all 100 tiles as creator-held / free-to-claim — exactly
    // the symptom on grid `0x5089...3` after the v2.2 migration.
    //
    // `isCreatorSeat` already encodes "holder is creator AND deposit is 0
    // AND grid not graduated", which matches the lazy-seat shape exactly,
    // so we reuse it (gated on `isV22` to keep V21 grids untouched — V21
    // has eager seat rows and no lazy-creator placeholder).
    const isV22LazyCreatorPlaceholder = isV22 && isCreatorSeat;
    const isReserved =
      !!reservation &&
      (!resolvedHolder || isV22LazyCreatorPlaceholder) &&
      !hasForfeitSignal;
    // `isOwnReservation` lights up the purple-rim "claim me" affordance
    // (see `TileOwnedRimOverlay`'s `'claimable'` variant). Match against
    // the wagmi-active address AND every wallet linked to the viewer's
    // Privy session: a holder whose V1 EOA is the reservation claimant
    // but who logs in via a social account (Privy auto-provisions an
    // embedded wallet) ends up with `address = embedded`, never the
    // claimant — without the linked-wallet union the rim never paints
    // and the tile reads as someone else's reservation. See `useWallet.
    // linkedAddresses` for the source of truth (already lowercased).
    const claimantLc = reservation?.claimant.toLowerCase();
    const isOwnReservation =
      isReserved &&
      !!claimantLc &&
      ((!!address && claimantLc === address.toLowerCase()) ||
        (!!viewerAddresses && viewerAddresses.has(claimantLc)));

    const antiSnipeEndsAtValue = (() => {
      if (!effectiveForfeitTs) return undefined;
      const endsAt = isV22
        ? (getDutchEndsAt(effectiveForfeitTs, v22Window) ?? undefined)
        : effectiveForfeitTs + antiSnipeWindow;
      if (endsAt === undefined) return undefined;
      return endsAt > now ? endsAt : undefined;
    })();

    const forfeitPlinthPayload =
      !resolvedHolder && antiSnipeEndsAtValue != null
        ? (() => {
            const gh = forfeitOldHolderBySeat.get(seat.seatIndex);
            if (!gh || gh.toLowerCase() === ZERO_ADDR) return {};
            const forfeitPlinthIsViewer =
              !!address && gh.toLowerCase() === address.toLowerCase();
            return {
              forfeitPlinthHolder: gh,
              ...(forfeitPlinthIsViewer ? { forfeitPlinthIsViewer: true as const } : {}),
            };
          })()
        : {};

    return {
      seatIndex: seat.seatIndex,
      // When a V22 lazy seat is also a whitelist reservation, drop the
      // creator-as-holder placeholder before it reaches the renderers.
      // `tileTier` short-circuits on `isReserved` regardless of `holder`,
      // but the 2D / 3D tile components still gate avatar/price/click
      // affordances on `!seat.holder`, so we have to clear it here for
      // the reservation to render (avatar overlay, claim countdown chip,
      // suppressed price strip / `+ OPEN` chip). Without this, the tile
      // tier would correctly be "reserved" but the visuals would still
      // paint the creator as the seat holder.
      holder: isReserved ? null : resolvedHolder,
      holderName: isReserved
        ? null
        : isOwn && displayName ? displayName : (seat.holderIdentity?.displayName ?? null),
      holderAvatar: isReserved
        ? null
        : getHighResAvatarUrl(
            isOwn && avatarUrl ? avatarUrl : seat.holderIdentity?.avatarUrl
          ),
      holderLevel: isReserved
        ? undefined
        : isOwn && userLevel !== undefined ? userLevel : seat.holderIdentity?.level,
      selfAssessedPrice: price,
      balance: currentDeposit,
      runwaySeconds,
      claimedAt: seat.heldSince || seat.createdAt || 0,
      lastUpdateTime: lastTax,
      lastPriceChangeTime: seat.lastPriceChangeTime,
      createdAt: seat.createdAt,
      // Trust forfeit flags only when the signal is authoritative. Without
      // `rpcCurrentBalance`, `currentDeposit` is a client-side projection
      // of stale Envio `deposit + lastTaxTime` math, which drifts to zero
      // on any seat whose tax state we haven't seen an event for recently —
      // the on-chain balance may still be healthy. Flagging those as
      // forfeited/protected painted seats the user confirmed were fine
      // on-chain. Safer: only surface forfeit when we have a live RPC
      // balance OR a confirmed forfeit event in `forfeitTimeBySeat`
      // **or** on-chain `rpcForfeitedAt`.
      isForfeited:
        !isCreatorSeat &&
        seat.isActive &&
        hasHolder &&
        currentDeposit <= 0n &&
        (hasRpcBalance || hasForfeitSignal),
      isOwn: isOwn || isOwnReservation,
      // A reserved lazy seat is logically "not yet held by anyone", so it
      // must not flow through the creator-mintable / no-tax branches that
      // `isCreatorSeat` unlocks downstream (price chip, claim drawer
      // routing, holders-panel rollup). Forcing `false` here keeps the
      // tile in the `reserved` tier exclusively.
      isCreatorSeat: isReserved ? false : isCreatorSeat,
      isPendingForfeit:
        !isCreatorSeat &&
        hasHolder &&
        currentDeposit <= 0n &&
        !hasForfeitSignal &&
        hasRpcBalance,
      // `antiSnipeEndsAt` carries different semantics depending on the
      // grid generation:
      //   - V21: end of the 5-min anti-snipe priority waterfall window.
      //          Drawers + overlays on V21 grids slice this into
      //          per-priority-holder buckets and gate the buyout button.
      //   - V22: end of the per-grid Dutch decay window
      //          (`forfeitedAt + forfeitureDuration`). No priority
      //          waterfall — anyone can buy at the live decayed price
      //          via `_dutchPrice`. Drawers + overlays gate the V21
      //          priority UI on `!isV22Grid(managerAddress)` and
      //          render the Dutch decay surface instead.
      // Field name is intentionally preserved for now to keep the
      // surface area of this PR focused; rename to `forfeitWindowEndsAt`
      // is tracked as a follow-up.
      antiSnipeEndsAt: antiSnipeEndsAtValue,
      ...forfeitPlinthPayload,
      // `forfeitedAt` (V22-only) — surface the snapshot timestamp so
      // V22 surfaces (`<DutchAuctionPriceChip>`) can plug it straight
      // into `getDutchPrice` / `getDutchProgress` without having to
      // back-derive it from `antiSnipeEndsAt - forfeitureDuration`
      // (which loses precision when `forfeitureDuration` is
      // per-grid-overridden). Skipped on V21 grids — the priority
      // waterfall surfaces don't need it.
      forfeitedAt: isV22 && effectiveForfeitTs > 0 ? effectiveForfeitTs : undefined,
      gridUsesDutchAuction: isV22,
      forfeitureWindowSec: isV22 ? v22Window : undefined,
      ...(isReserved ? {
        isReserved: true,
        reservedFor: reservation!.claimant,
        claimDeadline,
        reservedForName: reservation!.claimantIdentity?.displayName,
        reservedForAvatar: getHighResAvatarUrl(reservation!.claimantIdentity?.avatarUrl)
          || generateGridAvatarUrl(reservation!.claimant),
      } : {}),
    };
  });
}

/**
 * Seats whose self-assessed price should contribute to the displayed grid floor.
 *
 * The floor is meant to read as "lowest price among seats that are
 * **actively paying Harberger tax and not in any forfeit lifecycle**".
 * The previous predicate excluded the obvious forfeit paths but still
 * folded two non-tax-paying buckets back into the min:
 *
 *   1. **Creator seats** — primary-mint inventory the grid creator hasn't
 *      sold yet. `isCreatorSeat` ⇒ deposit is 0n on V22 (creators are
 *      Harberger-exempt pre-graduation, see `isPrimaryMintCreatorSeat`
 *      in `use-lobby-header-stats.ts`). Their `selfAssessedPrice` is
 *      the *initial listing price* the creator chose at launch — letting
 *      it pin the floor meant a 100-seat grid with a single $50 holder
 *      and 99 unsold $5,000 creator tiles read as a "$50 floor" only
 *      after the user explicitly bought; before that the chip surfaced
 *      $5,000 even though no one was paying tax against it.
 *   2. **Tax-depleted seats** (`balance <= 0n`) — the holder's deposit
 *      has run out so they're not paying tax anymore; the runway is at
 *      zero and the seat is one `pokeTax` / `forfeit` call away from
 *      being cleared. `isPendingForfeit` already catches the V22 path
 *      (where we have an RPC balance to detect zero deposit), but on
 *      V21 grids and on V22 rows lacking `rpcCurrentBalance` the
 *      `isPendingForfeit` flag is intentionally suppressed (see the
 *      "Trust forfeit flags only when the signal is authoritative"
 *      comment in `buildSeatData`). Without an explicit `balance > 0n`
 *      gate here, those seats kept dragging the floor down even though
 *      they were effectively forfeit.
 *
 * Both gates mirror the user's stated rule ("seats paying tax and not
 * forfeit") and align this helper with `creator-grid-card.tsx` /
 * `useLobbyHeaderStats`'s primary-mint creator-seat exclusion.
 */
export function seatCountsTowardDisplayedFloor(seat: SeatData, nowSec: number): boolean {
  if (!seat.holder) return false;
  if (seat.isForfeited || seat.isPendingForfeit) return false;
  if (seat.isCreatorSeat) return false;
  if (seat.balance <= 0n) return false;
  const ends = seat.antiSnipeEndsAt;
  if (ends != null && ends > nowSec) return false;
  return true;
}

export function computeFloorAndTakeover(seats: SeatData[]): {
  computedFloor: bigint;
  computedTakeover: bigint;
} {
  const nowSec = Math.floor(Date.now() / 1000);
  const activeSeats = seats.filter((s) => s.holder);
  if (activeSeats.length === 0) return { computedFloor: 0n, computedTakeover: 0n };

  // Floor = lowest listed price among seats that are actively tradable at their ask —
  // not in forfeit limbo, pending on-chain poke, or inside a reclaim auction window.
  const pricedForFloor = activeSeats.filter((s) => seatCountsTowardDisplayedFloor(s, nowSec));
  const computedFloor =
    pricedForFloor.length === 0
      ? 0n
      : pricedForFloor.reduce(
          (min, s) => (s.selfAssessedPrice < min ? s.selfAssessedPrice : min),
          pricedForFloor[0].selfAssessedPrice,
        );
  const computedTakeover = activeSeats.reduce((sum, s) => sum + s.selfAssessedPrice, 0n);
  return { computedFloor, computedTakeover };
}

export function derivePriorityHolders(rawSeats: V2GridSeat[], creator?: string): PriorityHolder[] {
  if (!rawSeats.length) return [];

  const creatorLower = creator?.toLowerCase();
  const holderMap = new Map<string, PriorityHolder>();

  for (const seat of rawSeats) {
    if (!seat.holder) continue;
    const addr = seat.holder.toLowerCase();
    if (addr === creatorLower) continue;

    const existing = holderMap.get(addr);
    if (!existing || seat.heldSince < existing.heldSince) {
      holderMap.set(addr, {
        address: seat.holder,
        heldSince: seat.heldSince || seat.createdAt || 0,
        displayName: seat.holderIdentity?.displayName,
        avatarUrl: seat.holderIdentity?.avatarUrl,
        level: seat.holderIdentity?.level,
      });
    }
  }

  return Array.from(holderMap.values()).sort((a, b) => a.heldSince - b.heldSince);
}

export function resolvePrimaryCoinAddress(
  assets: V2GridAsset[],
  yieldAddr?: string
): string | undefined {
  if (!assets.length) return undefined;
  const asset = assets[0];
  const t0 = asset.token0Address?.toLowerCase();
  const t1 = asset.token1Address?.toLowerCase();
  if (!t0 && !t1) return asset.id;

  const t0Sym = asset.token0Symbol?.toUpperCase() ?? '';
  const t1Sym = asset.token1Symbol?.toUpperCase() ?? '';
  const t0IsBase = BASE_SYMBOLS.has(t0Sym);
  const t1IsBase = BASE_SYMBOLS.has(t1Sym);

  if (t0IsBase && !t1IsBase && t1) return t1;
  if (t1IsBase && !t0IsBase && t0) return t0;

  const yieldLower = yieldAddr?.toLowerCase();
  if (t0 && t0 !== yieldLower) return t0;
  if (t1 && t1 !== yieldLower) return t1;
  return t0 || t1 || asset.id;
}

export function resolveYieldTokenSymbol(
  yieldAddr: string | undefined,
  assets: V2GridAsset[]
): string | undefined {
  const yieldLower = yieldAddr?.toLowerCase();
  if (!yieldLower) return 'ETH';
  if (KNOWN_YIELD_SYMBOLS[yieldLower]) return KNOWN_YIELD_SYMBOLS[yieldLower];

  for (const asset of assets) {
    if (!asset.pairLabel) continue;
    const parts = asset.pairLabel.replace(/ LP$/, '').split('/');
    if (parts.length !== 2) continue;
    if (asset.token0Address?.toLowerCase() === yieldLower) return parts[0];
    if (asset.token1Address?.toLowerCase() === yieldLower) return parts[1];
  }

  return undefined;
}

export function convertRewardEventsToDisplay(args: {
  rewardEvents: Array<{ amount: string; adapterType?: string } & Record<string, unknown>>;
  hasYieldPrice: boolean;
  yieldPriceUsd: number;
  yieldDecimals: number;
  ethPrice?: number;
  tokenToUsd: (raw: bigint, decimals: number, priceUsd: number) => number;
}) {
  const { rewardEvents, hasYieldPrice, yieldPriceUsd, yieldDecimals, ethPrice, tokenToUsd } = args;

  if (hasYieldPrice) {
    return rewardEvents.map((r) => {
      const decimals = r.adapterType === 'flaunch' ? 18 : yieldDecimals;
      const price = r.adapterType === 'flaunch' ? (ethPrice || yieldPriceUsd) : yieldPriceUsd;
      const usdValue = tokenToUsd(safeBigInt(r.amount), decimals, price);
      return { ...r, amount: (usdValue * 1e18).toFixed(0) };
    });
  }

  return rewardEvents.map((r) => {
    const decimals = r.adapterType === 'flaunch' ? 18 : yieldDecimals;
    const human = Number(safeBigInt(r.amount)) / 10 ** decimals;
    return { ...r, amount: (human * 1e18).toFixed(0) };
  });
}

export function createEmptySeat(index: number): SeatData {
  return {
    seatIndex: index,
    holder: null,
    selfAssessedPrice: 0n,
    balance: 0n,
    runwaySeconds: 0,
    isForfeited: false,
    isOwn: false,
    gridUsesDutchAuction: false,
  };
}
